import React, { useState } from "react";
import validator from "validator";
import InputField from "../../shared-components/InputField";
import AddButton from "../../shared-components/AddButton";
import Button, { ButtonColorTypes } from "../../shared-components/Button";
import { classNames, uniqueUUID } from "../../utils";
import { XIcon } from "../../shared-components/icons";
import { PersonEditFormState } from "./helpers";
import { isValidBirthDate } from "./helpers";

interface PersonEditFormProps {
  formState: PersonEditFormState;
  setFormState: React.Dispatch<React.SetStateAction<PersonEditFormState>>;
  onSave: () => void;
  onCancel: () => void;
  hasUnsavedChanges: boolean;
  sanitizeName: (s: string, max?: number) => string;
  sanitizeAlias: (s: string, max?: number) => string;
  isSaving?: boolean;
}

const PersonEditForm: React.FC<PersonEditFormProps> = ({
  formState,
  setFormState,
  onSave,
  onCancel,
  hasUnsavedChanges,
  sanitizeName: sanitizeNameProp,
  sanitizeAlias: sanitizeAliasProp,
  isSaving = false,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const primaryNameValid =
    formState.primary.firstName.trim() !== "" &&
    formState.primary.lastName.trim() !== "";
  const nonEmptyEmails = formState.emails.filter((e) => e.email.trim() !== "");
  const emailsValid =
    nonEmptyEmails.length >= 1 &&
    nonEmptyEmails.every((e) => validator.isEmail(e.email));
  const birthDateValid = isValidBirthDate(formState.birthDate);
  const isValid = primaryNameValid && emailsValid && birthDateValid;

  const updatePrimary = (
    field: "firstName" | "middleName" | "lastName",
    value: string
  ) => {
    setFormState((s) => ({
      ...s,
      primary: { ...s.primary, [field]: sanitizeNameProp(value) },
    }));
    setIsDirty(true);
  };

  const updateAlias = (
    idx: number,
    field: "firstName" | "middleName" | "lastName",
    value: string
  ) => {
    setFormState((s) => {
      const next = [...s.aliases];
      next[idx] = { ...next[idx], [field]: sanitizeAliasProp(value) };
      return { ...s, aliases: next };
    });
    setIsDirty(true);
  };

  const addAlias = () => {
    setFormState((s) => ({
      ...s,
      aliases: [
        ...s.aliases,
        { key: uniqueUUID(), firstName: "", middleName: "", lastName: "" },
      ],
    }));
    setIsDirty(true);
  };

  const removeAlias = (idx: number) => {
    setFormState((s) => ({
      ...s,
      aliases: s.aliases.filter((_, i) => i !== idx),
    }));
    setIsDirty(true);
  };

  const updateEmail = (idx: number, value: string) => {
    setFormState((s) => {
      const next = [...s.emails];
      next[idx] = { ...next[idx], email: value };
      return { ...s, emails: next };
    });
    setIsDirty(true);
  };

  const addEmail = () => {
    setFormState((s) => ({
      ...s,
      emails: [...s.emails, { key: uniqueUUID(), email: "" }],
    }));
    setIsDirty(true);
  };

  const removeEmail = (idx: number) => {
    setFormState((s) => {
      if (s.emails.length <= 1) return s;
      return {
        ...s,
        emails: s.emails.filter((_, i) => i !== idx),
      };
    });
    setIsDirty(true);
  };

  const updatePhone = (idx: number, value: string) => {
    setFormState((s) => {
      const next = [...s.phones];
      next[idx] = { ...next[idx], phone: value };
      return { ...s, phones: next };
    });
    setIsDirty(true);
  };

  const addPhone = () => {
    setFormState((s) => ({
      ...s,
      phones: [...s.phones, { key: uniqueUUID(), phone: "" }],
    }));
    setIsDirty(true);
  };

  const removePhone = (idx: number) => {
    setFormState((s) => ({
      ...s,
      phones: s.phones.filter((_, i) => i !== idx),
    }));
    setIsDirty(true);
  };

  return (
    <div>
      <div className="text-lg font-bold text-darkBlue mb-4">
        Person Information
      </div>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg bg-gray-100">
        <div className="flex gap-2">
          <div className="flex-1 flex flex-col gap-2">
            <InputField
              value={formState.primary.firstName}
              handleChange={(e) => updatePrimary("firstName", e.target.value)}
              name="primary-first"
              placeholder=""
              label="First Name*"
              labelClassName="font-thin"
              classes="flex-1"
              className={classNames(
                isDirty && !primaryNameValid ? "border-rose-600" : "",
                "w-full"
              )}
            />
            {isDirty && !primaryNameValid && (
              <div className="text-red-500 text-sm">
                First and last name are required
              </div>
            )}
          </div>
          <InputField
            value={formState.primary.middleName}
            handleChange={(e) => updatePrimary("middleName", e.target.value)}
            name="primary-middle"
            placeholder=""
            label="Middle Name"
            labelClassName="font-thin"
            classes="flex-1"
            className="w-full"
          />
          <div className="flex-1">
            <InputField
              value={formState.primary.lastName}
              handleChange={(e) => updatePrimary("lastName", e.target.value)}
              name="primary-last"
              placeholder=""
              label="Last Name*"
              labelClassName="font-thin"
              classes="flex-1"
              className={classNames(
                isDirty && !primaryNameValid ? "border-rose-600" : "",
                "w-full"
              )}
            />
          </div>
        </div>
        <div className="flex-1 pb-4 pt-4">Aliases</div>
        {formState.aliases.length > 0 && (
          <div className="flex flex-col gap-2 mb-2">
            {formState.aliases.map((a, idx) => (
              <div key={a.key} className="flex flex-nowrap items-start gap-4">
                <div className="flex-1">
                  <InputField
                    value={a.firstName}
                    handleChange={(e) =>
                      updateAlias(idx, "firstName", e.target.value)
                    }
                    name={`alias-fn-${idx}`}
                    placeholder=""
                    label="First Name"
                    labelClassName="font-thin"
                    classes="flex-1"
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <InputField
                    value={a.middleName}
                    handleChange={(e) =>
                      updateAlias(idx, "middleName", e.target.value)
                    }
                    name={`alias-mn-${idx}`}
                    placeholder=""
                    label="Middle Name"
                    labelClassName="font-thin"
                    classes="flex-1"
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <InputField
                    value={a.lastName}
                    handleChange={(e) =>
                      updateAlias(idx, "lastName", e.target.value)
                    }
                    name={`alias-ln-${idx}`}
                    placeholder=""
                    label="Last Name"
                    labelClassName="font-thin"
                    classes="flex-1"
                    className="w-full"
                  />
                </div>
                <span className="pl-2 h-0 w-10 pt-8">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-8 h-8 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => removeAlias(idx)}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && removeAlias(idx)}
                    aria-label="Remove alias"
                  >
                    <XIcon />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <AddButton
            text="Add Aliases"
            onClick={addAlias}
            className="text-gradBlue hover:opacity-80 transition-opacity"
          />
        </div>
        <div className="flex gap-4 pt-4">
          <div className="flex-1">
            <div className="flex flex-col gap-2">
              {formState.emails.map((e, idx) => (
                <div key={e.key} className="flex items-end gap-2">
                  <InputField
                    value={e.email}
                    handleChange={(ev) => updateEmail(idx, ev.target.value)}
                    name={`email-${idx}`}
                    placeholder="email@example.com"
                    label={idx === 0 ? "Email*" : "Email"}
                    labelClassName="font-thin"
                    type="email"
                    classes="flex-1"
                    className={classNames(
                      isDirty && e.email && !validator.isEmail(e.email)
                        ? "border-rose-600"
                        : "",
                      "w-full"
                    )}
                  />
                  {formState.emails.length > 1 && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-8 h-8 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                      onClick={() => removeEmail(idx)}
                      tabIndex={0}
                      onKeyDown={(ev) =>
                        ev.key === "Enter" && removeEmail(idx)
                      }
                      aria-label="Remove email"
                    >
                      <XIcon />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <AddButton
                text="Add Email"
                onClick={addEmail}
                className="text-gradBlue hover:opacity-80 transition-opacity"
              />
            </div>
            {isDirty && !emailsValid && (
              <div className="text-red-500 text-sm mt-2">
                {nonEmptyEmails.length === 0
                  ? "At least one email is required"
                  : "Please enter valid email addresses"}
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex flex-col gap-2">
              {formState.phones.map((p, idx) => (
                <div key={p.key} className="flex items-end gap-2">
                  <InputField
                    value={p.phone}
                    handleChange={(ev) => updatePhone(idx, ev.target.value)}
                    name={`phone-${idx}`}
                    placeholder="Phone number"
                    label={idx === 0 ? "Phone Number" : "Phone"}
                    labelClassName="font-thin"
                    classes="flex-1"
                    className="w-full"
                  />
                  {idx > 0 && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-8 h-8 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                      onClick={() => removePhone(idx)}
                      tabIndex={0}
                      onKeyDown={(ev) =>
                        ev.key === "Enter" && removePhone(idx)
                      }
                      aria-label="Remove phone"
                    >
                      <XIcon />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <AddButton
                text="Add Phone Number"
                onClick={addPhone}
                className="text-gradBlue hover:opacity-80 transition-opacity"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <div className="flex-1">
            <InputField
              value={formState.birthDate}
              handleChange={(e) => {
                setFormState((s) => ({ ...s, birthDate: e.target.value }));
                setIsDirty(true);
              }}
              name="birth-date"
              placeholder=""
              label="Date of Birth"
              labelClassName="font-thin"
              type="date"
              max={new Date().toISOString().split("T")[0]}
              classes="w-full"
              className={classNames(
                isDirty && !birthDateValid ? "border-rose-600" : "",
                "w-full"
              )}
            />
            {isDirty && !birthDateValid && (
              <div className="text-red-500 text-sm mt-2">
                Please enter a valid date
              </div>
            )}
          </div>
          <div className="flex-1" />
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-8">
        <Button
          text="Cancel"
          colorType={ButtonColorTypes.cancel}
          onClick={onCancel}
          disabled={isSaving}
        />
        <Button
          text={isSaving ? "Saving..." : "Save"}
          colorType={ButtonColorTypes.warning}
          onClick={onSave}
          disabled={!hasUnsavedChanges || !isValid || isSaving}
        />
      </div>
    </div>
  );
};

export default PersonEditForm;
